import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./HRTalk.css";
import Login from "../../Modal/Login/Login";

/* ─── ENV VARS ─── */
const RAZORPAY_KEY_ID    = process.env.REACT_APP_RAZORPAY_KEY_ID    || "rzp_test_XXXXXXXXXXXXXX";
const EJS_SERVICE_ID     = process.env.REACT_APP_EJS_SERVICE_ID     || "service_5h4f7wn";
const EJS_TEMPLATE_ADMIN = process.env.REACT_APP_EJS_TEMPLATE_ADMIN || "template_uxt1ce2";
const EJS_TEMPLATE_USER  = process.env.REACT_APP_EJS_TEMPLATE_USER  || "template_92je0m8";
const EJS_PUBLIC_KEY     = process.env.REACT_APP_EJS_PUBLIC_KEY     || "ZoLf9a2u2Ghq07oKT";
const ADMIN_EMAIL        = process.env.REACT_APP_ADMIN_EMAIL         || "ihn.socialmediaa@gmail.com";
const API_BASE           = process.env.REACT_APP_API_URL            || "/api";

/* ─── DESIGN TOKENS ─── */
const T = {
  brand: "#263012", brandHover: "#1d240e", brandAccent: "#263012",
  oliveGreen: "#4a5e20", surface: "#ffffff", surface2: "#f8f8f7",
  surface3: "#f2f2f0", rule: "#e8e8e6", ink1: "#0f0f0f",
  ink2: "#263012", ink3: "#3d3d3d", ink4: "#6b6b6b",
  ink5: "#9e9e9e", ink6: "#c4c4c4", green: "#16a34a", red: "#dc2626",
  font: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const COLORS = ["#263012", "#4a5e20", "#6b7f3a", "#8fa050"];

/* ─── HOW IT WORKS — static UI copy ─── */
const HOW_IT_WORKS = [
  { num: "01", icon: "🔍", tag: "Discover", title: "Browse HR Experts",
    desc: "Find experts by skill and availability.",
    detail: "Each profile shows ratings and session focus areas." },
  { num: "02", icon: "💳", tag: "Plan",     title: "Choose & Pay",
    desc: "Select a session plan and complete payment.",
    detail: "First session is free (once per account). After that, a paid plan is required to unlock slot booking." },
  { num: "03", icon: "📅", tag: "Schedule", title: "Pick Your Slot",
    desc: "After payment, choose an available date and time.",
    detail: "Slot availability is controlled by admin in real-time. You can only book after payment is confirmed." },
  { num: "04", icon: "⭐", tag: "Rate",     title: "Rate Your Session",
    desc: "Share feedback after your meeting.",
    detail: "Ratings are saved and the expert's average score updates automatically." },
];

/* ─── HELPERS ─── */
function getToken() {
  const token =
    localStorage.getItem("th_token") ||
    localStorage.getItem("authToken") ||
    "";
  if (!token) return "";
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp && Date.now() > payload.exp * 1000) {
      ["th_token", "authToken", "th_user", "authUser"].forEach(k =>
        localStorage.removeItem(k)
      );
      return "";
    }
  } catch {
    return "";
  }
  return token;
}

function getStoredUser() {
  const raw =
    localStorage.getItem("th_user") ||
    localStorage.getItem("authUser") ||
    null;
  if (!raw) return null;
  try {
    const u = JSON.parse(raw);
    // Normalize name — app stores firstName/lastName not name
    if (!u.name && (u.firstName || u.lastName)) {
      u.name = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    }
    return u;
  } catch {
    return null;
  }
}

async function hrtalkFetch(path, { method = "GET", body, auth = false } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (auth) {
    if (!token) throw new Error("Please login as a candidate to continue.");
    headers.Authorization = `Bearer ${token}`;
  } else if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(path, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const payload  = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false)
    throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload;
}

function formatMoney(amount) {
  return amount <= 0 ? "Free" : `INR ${Number(amount).toLocaleString("en-IN")}`;
}

/* ─── SLOT GENERATOR ─── */
function buildSlotsFromTimings(timings = [], slotDurationMins = 30) {
  const now = new Date();
  const slots = [];
  const SLOT_GAP = slotDurationMins * 60 * 1000;

  for (let d = 0; d < 7; d++) {
    const base = new Date(now);
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + d);
    const dow = base.getDay();

    const dayTimings = timings.filter(t => Number(t.day_of_week) === dow && t.is_active);
    if (!dayTimings.length) continue;

    for (const timing of dayTimings) {
      const [sh, sm] = String(timing.start_time || "09:00").split(":").map(Number);
      const [eh, em] = String(timing.end_time || "18:00").split(":").map(Number);

      const windowStart = new Date(base);
      windowStart.setHours(sh, sm, 0, 0);
      const windowEnd = new Date(base);
      windowEnd.setHours(eh, em, 0, 0);

      let cursor = new Date(windowStart);
      let idx = 0;
      while (cursor.getTime() + SLOT_GAP <= windowEnd.getTime()) {
        if (!(d === 0 && cursor.getTime() <= now.getTime() + 10 * 60 * 1000)) {
          const h = cursor.getHours();
          const m = cursor.getMinutes();
          const suffix = h >= 12 ? "PM" : "AM";
          const hour12 = h % 12 === 0 ? 12 : h % 12;

          let dayLabel;
          if (d === 0) {
            dayLabel = "Today";
          } else if (d === 1) {
            dayLabel = "Tomorrow";
          } else {
            dayLabel = cursor.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
          }

          slots.push({
            id: `slot-${dow}-${sh}-${sm}-${d}-${idx}`,
            slotKey: `slot-${cursor.toISOString().slice(0, 16)}`,
            day: dayLabel,
            time: `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${suffix}`,
            startISO: cursor.toISOString(),
          });
          idx++;
        }
        cursor = new Date(cursor.getTime() + SLOT_GAP);
      }
    }
  }
  return slots;
}

/* ─── EMAILJS ─── */
let emailReady = false;
async function sendViaEmailJS(templateId, params) {
  if (!emailReady) {
    await new Promise((resolve, reject) => {
      if (window.emailjs) {
        window.emailjs.init({ publicKey: EJS_PUBLIC_KEY });
        emailReady = true; resolve(); return;
      }
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
      s.onload  = () => { window.emailjs.init({ publicKey: EJS_PUBLIC_KEY }); emailReady = true; resolve(); };
      s.onerror = () => reject(new Error("Failed to load EmailJS SDK"));
      document.head.appendChild(s);
    });
  }
  return window.emailjs.send(EJS_SERVICE_ID, templateId, params);
}

/* ─── RAZORPAY ─── */
function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) { resolve(window.Razorpay); return; }
    const s = document.createElement("script");
    s.src     = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload  = () => resolve(window.Razorpay);
    s.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(s);
  });
}

/* ─── AOS HOOK ─── */
function useAOS(deps = []) {
  useEffect(() => {
    const els = document.querySelectorAll("[data-aos]");
    const io  = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add("aos-in", "aos--in");
        else e.target.classList.remove("aos-in", "aos--in");
      }),
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/* ─── SHARED STYLES ─── */
const S = {
  errorBox: {
    marginBottom: 10, padding: "10px 13px",
    background: "rgba(220,38,38,.06)", border: "1px solid rgba(220,38,38,.20)",
    borderRadius: 8, fontSize: ".75rem", color: "#dc2626", fontFamily: T.font,
  },
  successBox: {
    marginBottom: 10, padding: "10px 13px",
    background: "rgba(22,163,74,.06)", border: "1px solid rgba(22,163,74,.20)",
    borderRadius: 8, fontSize: ".75rem", color: "#16a34a", fontFamily: T.font,
  },
  confirmBanner: {
    background: "rgba(38,48,18,.06)", border: "1.5px solid rgba(38,48,18,.20)",
    borderRadius: 12, padding: "12px 15px", marginBottom: 16,
  },
  confirmBannerTitle: { fontSize: ".78rem", fontWeight: 700, color: T.brand, fontFamily: T.font },
  confirmBannerSub:   { fontSize: ".68rem", color: T.ink4, marginTop: 2, fontFamily: T.font },
  loginBox: {
    padding: "20px 18px", background: "rgba(38,48,18,.05)",
    border: "1.5px dashed rgba(38,48,18,.25)", borderRadius: 12,
    textAlign: "center", marginBottom: 10,
  },
  loginBoxTitle: { fontSize: ".82rem", fontWeight: 700, color: T.brand, marginBottom: 4, fontFamily: T.font },
  loginBoxSub: { fontSize: ".72rem", color: T.ink4, fontFamily: T.font },
};

/* ─── AVATAR ─── */
function Avatar({ name, avatarUrl, size, radius, className, style, children }) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => { setImgError(false); }, [avatarUrl]);

  const initials = name
    ? name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "HR";

  const bgColors = ["#263012", "#4a5e20", "#6b7f3a", "#3d5010", "#1d3808"];
  const bgColor  = bgColors[(name || "").codePointAt(0) % bgColors.length] || "#263012";

  const baseStyle = {
    width: size, height: size, borderRadius: radius, flexShrink: 0,
    overflow: "hidden",
    background: (!avatarUrl || imgError) ? bgColor : "#f2f5ea",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", boxShadow: "0 4px 14px rgba(0,0,0,.15)", ...style,
  };

  const hasValidUrl = avatarUrl && avatarUrl.length > 4 && !imgError;
  const origin = (API_BASE || "").replace(/\/api\/?$/, "");
  const finalSrc = (hasValidUrl && !avatarUrl.startsWith("http")) ? `${origin}${avatarUrl}` : avatarUrl;

  return (
    <div className={`hta-ava-wrap ${className}`} style={baseStyle}>
      {hasValidUrl ? (
        <img
          src={finalSrc} alt={name || "HR Expert"}
          onError={() => setImgError(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <span style={{
          fontSize: size * 0.30, fontWeight: 800, color: "#fff",
          fontFamily: T.font, letterSpacing: "-.02em", userSelect: "none",
        }}>{initials}</span>
      )}
      {children}
    </div>
  );
}
Avatar.propTypes = {
  name:      PropTypes.string,
  avatarUrl: PropTypes.string,
  size:      PropTypes.number,
  radius:    PropTypes.number,
  className: PropTypes.string,
  style:     PropTypes.object,
  children:  PropTypes.node,
};
Avatar.defaultProps = {
  name: "", avatarUrl: "", size: 64, radius: 18, className: "", style: {}, children: null,
};

/* ─── STARS ─── */
function Stars({ n }) {
  return (
    <span className="hta-stars">
      {[1,2,3,4,5].map((i) => (
        <span key={i} className={i <= Math.round(n || 0) ? "ht-star ht-star--on" : "ht-star"}>★</span>
      ))}
      <span className="hta-star-num">{Number(n || 0).toFixed(1)}</span>
    </span>
  );
}
Stars.propTypes = { n: PropTypes.number };
Stars.defaultProps = { n: 0 };

/* ─── LOADING SKELETON ─── */
function CardSkeleton() {
  return (
    <div className="hta-hr-card hta-hr-card--skeleton" aria-hidden="true">
      <div className="hta-hr-card__bar" />
      <div className="hta-hr-card__body">
        <div className="hta-hr-card__head">
          <div className="sk-circle" style={{ width: 64, height: 64, borderRadius: 16 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="sk-line" style={{ width: "60%", height: 14 }} />
            <div className="sk-line" style={{ width: "80%", height: 11 }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          {[70, 55, 80].map((w, i) => (
            <div key={`skel-tag-${w}-${i}`} className="sk-line" style={{ width: w, height: 22, borderRadius: 20 }} />
          ))}
        </div>
        <div className="sk-line" style={{ width: "100%", height: 11, marginTop: 14 }} />
        <div className="sk-line" style={{ width: "80%",  height: 11, marginTop: 6 }} />
        <div className="sk-line" style={{ width: "100%", height: 38, marginTop: 20, borderRadius: 10 }} />
      </div>
    </div>
  );
}

/* ─── EMPTY STATE ─── */
function EmptyState({ title, sub, icon }) {
  return (
    <div className="hta-empty" data-aos="zoom-in">
      <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>{icon}</div>
      <div className="hta-empty__title">{title}</div>
      <div className="hta-empty__sub">{sub}</div>
    </div>
  );
}
EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  sub:   PropTypes.string.isRequired,
  icon:  PropTypes.string,
};
EmptyState.defaultProps = { icon: "🔍" };

/* ─── ERROR STATE ─── */
function ErrorState({ message, onRetry }) {
  return (
    <div className="hta-empty" data-aos="zoom-in">
      <div style={{ fontSize: "2rem", marginBottom: 10 }}>⚠️</div>
      <div className="hta-empty__title">Failed to load</div>
      <div className="hta-empty__sub">{message}</div>
      {onRetry && (
        <button className="hta-btn-outline" onClick={onRetry} style={{ marginTop: 14 }}>
          Try Again
        </button>
      )}
    </div>
  );
}
ErrorState.propTypes = {
  message: PropTypes.string.isRequired,
  onRetry: PropTypes.func,
};
ErrorState.defaultProps = { onRetry: null };

/* ─── LOGIN REQUIRED GATE ─── */
function LoginGate({ action, onLoginClick }) {
  return (
    <div style={S.loginBox}>
      <div style={S.loginBoxTitle}>🔒 Login Required</div>
      <div style={S.loginBoxSub}>
        Please{" "}
        <button
          type="button"
          onClick={onLoginClick}
          style={{
            background: "none", border: "none", color: T.brand, fontWeight: 700,
            textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit",
          }}
        >
          login
        </button>{" "}
        as a candidate to {action}.
      </div>
    </div>
  );
}
LoginGate.propTypes = {
  action:       PropTypes.string.isRequired,
  onLoginClick: PropTypes.func.isRequired,
};

/* ─── PENDING SESSION BANNER ─── */
function PendingSessionBanner({ bookings, experts }) {
  if (!bookings?.length) return null;
  const pending = bookings.filter(b => b.status === "pending" || b.status === "confirmed");
  if (!pending.length) return null;

  return (
    <div className="hta-pending-wrap" data-aos="fade-up">
      <div className="hta-pending-inner">
        <div className="hta-pending-icon">🗓️</div>
        <div className="hta-pending-content">
          <div className="hta-pending-title">
            You have {pending.length} upcoming session{pending.length > 1 ? "s" : ""}
          </div>
          {pending.map((b) => {
            const expert = experts.find(e => e.id === b.hr_id);
            return (
              <div key={b.id ?? b.slot?.slot_key ?? b.hr_id} className="hta-pending-item">
                <span className="hta-pending-dot" />
                <span>
                  <strong>{expert?.name || "HR Expert"}</strong> —{" "}
                  {b.day_label || b.slot?.day_label} at {b.time_label || b.slot?.time_label}
                  <span className="hta-pending-badge">Pending</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
PendingSessionBanner.propTypes = {
  bookings: PropTypes.array.isRequired,
  experts:  PropTypes.array.isRequired,
};

/* ─── REVIEWS MODAL ─── */
function ReviewsModal({ hr, currentUser, onClose, onTriggerLogin }) {
  const [reviews,     setReviews]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [fetchError,  setFetchError]  = useState("");
  const [showForm,    setShowForm]    = useState(false);
  const [rating,      setRating]      = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText,  setReviewText]  = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [submitMsg,   setSubmitMsg]   = useState("");
  const [submitState, setSubmitState] = useState("idle");

  const token = getToken();
  const isLoggedIn = Boolean((currentUser || getStoredUser()) && token);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    setLoading(true);
    setFetchError("");
    hrtalkFetch(`/api/hrtalk/experts/${hr.id}/reviews`)
      .then(data => setReviews(Array.isArray(data.reviews) ? data.reviews : []))
      .catch(err  => setFetchError(err.message || "Failed to load reviews."))
      .finally(() => setLoading(false));
  }, [hr.id]);

  const handleSubmitReview = async () => {
    if (!rating) { setSubmitState("error"); setSubmitMsg("Please select a rating."); return; }
    setSubmitting(true); setSubmitMsg(""); setSubmitState("idle");
    try {
      await hrtalkFetch(`/api/hrtalk/experts/${hr.id}/reviews`, {
        method: "POST", auth: true,
        body: { rating, review_text: reviewText.trim() },
      });
      const user = currentUser || getStoredUser();
      setReviews(prev => [{
        id: Date.now(), candidate_name: user?.name || user?.firstName || "You",
        rating, review_text: reviewText.trim(), created_at: new Date().toISOString(),
      }, ...prev]);
      setSubmitState("saved");
      setSubmitMsg("✅ Thank you! Your review has been posted.");
      setShowForm(false); setRating(0); setReviewText("");
    } catch (err) {
      setSubmitState("error");
      setSubmitMsg(err.message || "Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="hta-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className="hta-modal">
        <div className="hta-modal__head">
          <Avatar name={hr.name} avatarUrl={hr.avatarUrl} size={46} radius={12} style={{ flexShrink: 0 }} />
          <div className="hta-modal__head-info">
            <div className="hta-modal__head-name">{hr.name}</div>
            <div className="hta-modal__head-role">{hr.role}</div>
          </div>
          <button className="hta-modal__close" onClick={onClose} aria-label="Close reviews">✕</button>
        </div>

        <div className="hta-modal__body">
          <div className="hta-expert-reviews__header" style={{ marginBottom: 16 }}>
            <div className="hta-expert-reviews__title">
              Reviews &amp; Ratings
              {avgRating && (
                <span className="hta-expert-reviews__avg">
                  <span style={{ color: "#f59e0b", marginRight: 3 }}>★</span>
                  {avgRating} <span style={{ color: T.ink5, fontWeight: 400 }}>({reviews.length})</span>
                </span>
              )}
            </div>
            {isLoggedIn ? (
              !showForm && (
                <button className="hta-review-add-btn" onClick={() => setShowForm(true)}>
                  + Write Review
                </button>
              )
            ) : (
              <div style={{ marginTop: 10 }}>
                <LoginGate action="write a review" onLoginClick={onTriggerLogin} />
              </div>
            )}
          </div>

          {showForm && isLoggedIn && (
            <div className="hta-review-form">
              <div className="hta-review-form__label">Your Rating</div>
              <div className="hta-review-stars-input">
                {[1,2,3,4,5].map(star => (
                  <button key={star}
                    className={`hta-star-btn ${(hoverRating || rating) >= star ? "active" : ""}`}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star)}>★</button>
                ))}
              </div>
              <textarea className="hta-review-textarea" rows={3}
                placeholder={`Share your experience with ${hr.name}…`}
                value={reviewText} onChange={e => setReviewText(e.target.value)} />
              {submitMsg && (
                <div style={{ fontSize: ".74rem", color: submitState === "saved" ? T.green : T.red, marginBottom: 8 }}>
                  {submitMsg}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="hta-btn-primary" style={{ fontSize: ".78rem", padding: "8px 16px" }}
                  onClick={handleSubmitReview} disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit Review"}
                </button>
                <button className="hta-btn-outline" style={{ fontSize: ".78rem", padding: "8px 14px" }}
                  onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {submitState === "saved" && !showForm && (
            <div style={{ ...S.successBox, marginBottom: 12 }}>{submitMsg}</div>
          )}

          {loading ? (
            <div className="hta-reviews-loading">Loading reviews…</div>
          ) : fetchError ? (
            <div style={S.errorBox}>{fetchError}</div>
          ) : reviews.length === 0 ? (
            <div className="hta-reviews-empty">No reviews yet. Be the first to review!</div>
          ) : (
            <div className="hta-reviews-list">
              {reviews.map((rev) => (
                <div key={rev.id} className="hta-review-item">
                  <div className="hta-review-item__top">
                    <div className="hta-review-item__ava">
                      {(rev.candidate_name || "U")[0].toUpperCase()}
                    </div>
                    <div className="hta-review-item__meta">
                      <div className="hta-review-item__name">{rev.candidate_name || "Candidate"}</div>
                      <div className="hta-review-item__stars">
                        {[1,2,3,4,5].map(s => (
                          <span key={s} style={{ color: s <= rev.rating ? "#f59e0b" : "#e5e7eb", fontSize: ".8rem" }}>★</span>
                        ))}
                      </div>
                    </div>
                    <div className="hta-review-item__date">
                      {new Date(rev.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </div>
                  {rev.review_text && <p className="hta-review-item__text">{rev.review_text}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
ReviewsModal.propTypes = {
  hr:             PropTypes.object.isRequired,
  currentUser:    PropTypes.object,
  onClose:        PropTypes.func.isRequired,
  onTriggerLogin: PropTypes.func.isRequired,
};
ReviewsModal.defaultProps = { currentUser: null };

/* ─── PAYMENT STEP ─── */
function PaymentStep({ hr, plan, name, email, onSuccess, onBack, externalError }) {
  const [payStatus, setPayStatus] = useState("idle");
  const [errMsg,    setErrMsg]    = useState("");
  const isProcessing = payStatus === "loading" || payStatus === "processing";
  const amountPaise  = Number(plan?.amountPaise || 0);
  const isFree       = plan?.isFree || amountPaise === 0;
  const isTestMode   = RAZORPAY_KEY_ID.includes("test");

  const handlePay = async () => {
    setErrMsg(""); setPayStatus("loading");
    if (isTestMode || isFree) {
      setPayStatus("success");
      onSuccess({ paymentId: isFree ? "FREE" : `TEST_PAY_${Date.now()}`, orderId: null });
      return;
    }
    try {
      const RazorpayClass = await loadRazorpay();
      setPayStatus("processing");
      const options = {
        key: RAZORPAY_KEY_ID, amount: amountPaise, currency: "INR",
        name: "TalentHive HRTalk",
        description: `${plan?.name || "Session"} with ${hr.name}`,
        prefill: { name, email },
        notes: { hr_name: hr.name, plan: plan?.id || "" },
        theme: { color: T.brand },
        handler: (response) => {
          setPayStatus("success");
          onSuccess({ paymentId: response.razorpay_payment_id, orderId: response.razorpay_order_id });
        },
      };
      const rzp = new RazorpayClass(options);
      rzp.on("payment.failed", (r) => {
        setPayStatus("error");
        setErrMsg(r?.error?.description || "Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      setPayStatus("error");
      setErrMsg(err.message || "Payment gateway failed to load.");
    }
  };

  let btnLabel;
  if (isProcessing) {
    btnLabel = "Processing…";
  } else if (isFree) {
    btnLabel = "Confirm Free Session →";
  } else if (isTestMode) {
    btnLabel = "Auto-Complete (Test) →";
  } else {
    btnLabel = `Pay ${plan?.price || formatMoney(plan?.priceInr || 0)} →`;
  }

  return (
    <div className="hta-mphase">
      <button className="hta-back-btn" onClick={onBack} disabled={isProcessing}>← Back</button>
      <h3 className="hta-mphase__title">{isFree ? "Confirm Free Session" : "Complete Payment"}</h3>
      <p className="hta-mphase__sub">
        {isFree
          ? "Your free first session. Confirm to unlock slot selection."
          : isTestMode
            ? "Test Mode: Payment will be auto-completed."
            : "Complete payment to unlock slot selection."}
      </p>
      <div className="hta-confirm-card" style={{ marginBottom: 16 }}>
        {[
          ["HR Expert", hr.name],
          ["Plan", `${plan?.name || ""} (${plan?.durationMins || 30} min)`],
          ["Amount", plan?.price || formatMoney(plan?.priceInr || 0)],
          ["Name", name],
          ["Email", email],
        ].map(([label, value]) => (
          <div className="hta-confirm-row" key={label}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </div>
      {(errMsg || externalError) && <div style={S.errorBox}>{errMsg || externalError}</div>}
      <button className="hta-btn-primary" onClick={handlePay} disabled={isProcessing}>
        {btnLabel}
      </button>
    </div>
  );
}
PaymentStep.propTypes = {
  hr:            PropTypes.object.isRequired,
  plan:          PropTypes.object.isRequired,
  name:          PropTypes.string.isRequired,
  email:         PropTypes.string.isRequired,
  onSuccess:     PropTypes.func.isRequired,
  onBack:        PropTypes.func.isRequired,
  externalError: PropTypes.string,
};
PaymentStep.defaultProps = { externalError: "" };

/* ─── SLOT STEP ─── */
function SlotStep({ slots, plan, onSelect, sending }) {
  const [slotId, setSlotId] = useState(null);
  const groupedSlots = useMemo(() => {
    return slots.reduce((acc, slot) => {
      if (!acc[slot.day]) acc[slot.day] = [];
      acc[slot.day].push(slot);
      return acc;
    }, {});
  }, [slots]);
  const selectedSlot = slots.find((s) => s.id === slotId) || null;

  if (!slots.length) {
    return (
      <div className="hta-mphase">
        <div style={S.successBox}>✅ Payment confirmed.</div>
        <EmptyState icon="📅" title="No slots available" sub="The admin hasn't opened any slots yet. Please check back soon." />
      </div>
    );
  }

  return (
    <div className="hta-mphase">
      <div style={{ ...S.successBox, marginBottom: 14 }}>✅ Payment confirmed — now pick your slot.</div>
      <h3 className="hta-mphase__title">Choose Your Slot</h3>
      <p className="hta-mphase__sub">Session duration: <strong>{plan?.durationMins || 30} min</strong>.</p>
      <div className="hta-slots-wrap">
        {Object.entries(groupedSlots).map(([day, daySlots]) => (
          <div key={day} className="hta-slots-day">
            <div className="hta-slots-day__label">{day}</div>
            <div className="hta-slots-day__row">
              {daySlots.map((slot) => (
                <button key={slot.id}
                  className={`sm-slot${slotId === slot.id ? " sel" : ""}`}
                  onClick={() => setSlotId(slot.id)}>{slot.time}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button className="hta-btn-primary" disabled={!slotId || sending}
        onClick={() => onSelect(selectedSlot)}>
        {sending ? "Confirming…" : "Confirm Slot & Book →"}
      </button>
    </div>
  );
}
SlotStep.propTypes = {
  slots:    PropTypes.array.isRequired,
  plan:     PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  sending:  PropTypes.bool.isRequired,
};

/* ─── CONFIRMED PHASE ─── */
function ConfirmedPhase({ hr, slot, plan, name, email, paymentId, bookingId, onClose }) {
  const [sessionState, setSessionState] = useState("confirmed");
  const [rating,       setRating]       = useState(0);
  const [reviewText,   setReviewText]   = useState("");
  const [ratingState,  setRatingState]  = useState("idle");
  const [ratingMsg,    setRatingMsg]    = useState("");

  const handleSubmitRating = async () => {
    if (!bookingId) { setRatingState("error"); setRatingMsg("Booking reference missing."); return; }
    if (rating < 1 || rating > 5) { setRatingState("error"); setRatingMsg("Please select 1–5 stars."); return; }
    setRatingState("saving"); setRatingMsg("");
    try {
      await hrtalkFetch(`/api/hrtalk/bookings/${bookingId}/rating`, {
        method: "POST", auth: true, body: { rating, review_text: reviewText.trim() },
      });
      setRatingState("saved");
      setRatingMsg("Thank you! Your rating has been saved.");
    } catch (err) {
      setRatingState("error");
      setRatingMsg(err.message || "Failed to save rating.");
    }
  };

  return (
    <div className="hta-mphase">
      <div style={S.confirmBanner}>
        <div style={S.confirmBannerTitle}>✅ Booking Confirmed</div>
        <div style={S.confirmBannerSub}>
          📧 Confirmation sent to <strong>{email}</strong> and the HR team.
          {paymentId && paymentId !== "FREE" && ` · Payment ID: ${paymentId}`}
        </div>
      </div>
      <div className="hta-confirm-card" style={{ marginBottom: 16 }}>
        {[
          ["HR Expert", hr.name], ["Role", hr.role],
          ["Slot", `${slot?.day || ""} ${slot?.time || ""}`],
          ["Duration", `${plan?.durationMins || 30} min`],
          ["Plan", plan?.name || ""], ["Candidate", `${name} (${email})`],
        ].map(([label, value]) => (
          <div className="hta-confirm-row" key={label}><span>{label}</span><strong>{value}</strong></div>
        ))}
      </div>
      <div style={{ ...S.successBox, marginBottom: 16 }}>
        📧 A session summary has been emailed to you and <strong>{hr.name}</strong>.
      </div>
      {sessionState === "confirmed" && (
        <button className="hta-btn-outline" onClick={() => setSessionState("ended")}>
          Mark Session as Completed
        </button>
      )}
      {sessionState === "ended" && (
        <div style={{ marginTop: 12 }}>
          <h3 className="hta-mphase__title">Rate Your HRTalk Session</h3>
          <p className="hta-mphase__sub">Your rating updates this expert&apos;s average score.</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[1,2,3,4,5].map((star) => (
              <button key={star} onClick={() => setRating(star)} style={{
                width: 34, height: 34, borderRadius: 8, cursor: "pointer",
                border: `1.5px solid ${rating >= star ? T.brand : T.rule}`,
                background: rating >= star ? "#f2f5ea" : T.surface,
                fontWeight: 700, color: rating >= star ? T.brand : T.ink5,
                fontFamily: T.font, fontSize: ".88rem", transition: "all .18s",
              }}>{star}</button>
            ))}
          </div>
          <textarea className="sm-inp" rows={3} placeholder="Add quick feedback (optional)"
            value={reviewText} onChange={(e) => setReviewText(e.target.value)}
            style={{ resize: "vertical", marginBottom: 10, fontFamily: T.font }} />
          {ratingMsg && (
            <div style={{ marginBottom: 10, fontSize: ".74rem", fontFamily: T.font,
              color: ratingState === "saved" ? T.green : T.red }}>{ratingMsg}</div>
          )}
          {ratingState !== "saved" && (
            <button className="hta-btn-primary" onClick={handleSubmitRating}
              disabled={ratingState === "saving"}>
              {ratingState === "saving" ? "Saving…" : "Submit Rating"}
            </button>
          )}
        </div>
      )}
      <button className="hta-btn-outline" onClick={onClose} style={{ marginTop: 12 }}>
        Back to Experts
      </button>
    </div>
  );
}
ConfirmedPhase.propTypes = {
  hr:        PropTypes.object.isRequired,
  slot:      PropTypes.object,
  plan:      PropTypes.object.isRequired,
  name:      PropTypes.string.isRequired,
  email:     PropTypes.string.isRequired,
  paymentId: PropTypes.string,
  bookingId: PropTypes.number,
  onClose:   PropTypes.func.isRequired,
};
ConfirmedPhase.defaultProps = { slot: null, paymentId: null, bookingId: null };

/* ─── SCHEDULE MODAL ─── */
function ScheduleModal({ hr, plans, slots, freeEligible, onClose, onBooked, currentUser, onTriggerLogin }) {
  const [phase,       setPhase]       = useState("planselect");
  const [plan,        setPlan]        = useState(null);
  const [name,        setName]        = useState("");
  const [email,       setEmail]       = useState("");
  const [errMsg,      setErrMsg]      = useState("");
  const [sending,     setSending]     = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);

  // Re-check token at render time, not stale closure
  const token = getToken();
  const isLoggedIn = Boolean((currentUser || getStoredUser()) && token);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const u = getStoredUser();
    if (u?.name)      setName(u.name);
    else if (u?.firstName) setName(`${u.firstName || ""} ${u.lastName || ""}`.trim());
    if (u?.email) setEmail(u.email);
  }, []);

  const freePlan   = plans.find((p) => p.isFree);
  const paidPlans  = plans.filter((p) => !p.isFree);
  const showFree   = freeEligible && Boolean(freePlan);
  const canSubmit  = Boolean(name.trim()) && email.includes("@") && !sending;

  const handlePaymentSuccess = (payResp) => { setPaymentInfo(payResp); setPhase("timeslot"); };

  const handleSlotConfirmed = async (selectedSlot) => {
    if (!selectedSlot || !plan) return;
    setSending(true); setErrMsg("");
    try {
      const user = getStoredUser();
      if (String(user?.role || "").toLowerCase() !== "candidate") {
        throw new Error("Please login as a candidate to book HRTalk.");
      }
      if (!selectedSlot.startISO) throw new Error("Invalid slot selected.");

      const saved = await hrtalkFetch("/api/hrtalk/bookings", {
        method: "POST", auth: true,
        body: {
          hr_id: hr.id,
          plan_id: plan.dbId > 0 ? plan.dbId : undefined,
          candidate_name:  name.trim(),
          candidate_email: email.trim(),
          payment_status: plan.isFree ? "free" : "paid",
          payment_id: paymentInfo?.paymentId || null,
          slot: {
            slot_key:  selectedSlot.slotKey || selectedSlot.id,
            day_label: selectedSlot.day,
            time_label: selectedSlot.time,
            start_iso: selectedSlot.startISO,
          },
        },
      }).catch(() => ({ booking: { id: null } }));

      const payLabel = paymentInfo?.paymentId ? `Paid · ID: ${paymentInfo.paymentId}` : "Free Session";
      const emailPayload = {
        user_name:  name.trim(),   user_email: email.trim(),
        hr_name:    hr.name,       hr_role:    hr.role,
        slot_time:  `${selectedSlot.day} at ${selectedSlot.time}`,
        duration:   `${plan.durationMins} min`,
        plan_name:  plan.name,
        payment_id: paymentInfo?.paymentId || "Free Session",
        message: [
          `Your HRTalk session has been confirmed.`, ``,
          `HR Expert : ${hr.name} (${hr.role})`,
          `Candidate : ${name.trim()} <${email.trim()}>`,
          `Session   : ${selectedSlot.day} at ${selectedSlot.time}`,
          `Duration  : ${plan.durationMins} minutes`,
          `Plan      : ${plan.name}`,
          `Payment   : ${payLabel}`,
        ].join("\n"),
      };

      await sendViaEmailJS(EJS_TEMPLATE_ADMIN, { ...emailPayload, to_email: ADMIN_EMAIL, from_name: name.trim() }).catch(() => {});
      await sendViaEmailJS(EJS_TEMPLATE_USER,  { ...emailPayload, to_email: email.trim(), from_name: hr.name }).catch(() => {});

      setBookingInfo({ bookingId: saved?.booking?.id || null, slot: selectedSlot });
      if (onBooked) onBooked();
      setPhase("done");
    } catch (err) {
      setErrMsg(err.message || "Failed to confirm booking. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="hta-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className="hta-modal">
        <div className="hta-modal__head">
          <Avatar name={hr.name} avatarUrl={hr.avatarUrl} size={46} radius={12} style={{ flexShrink: 0 }} />
          <div className="hta-modal__head-info">
            <div className="hta-modal__head-name">{hr.name}</div>
            <div className="hta-modal__head-role">{hr.role}</div>
          </div>
          <button className="hta-modal__close" onClick={onClose} aria-label="Close schedule modal">✕</button>
        </div>

        <div className="hta-modal__body">
          {isLoggedIn ? (
            <>
              {phase === "planselect" && (
                <div className="hta-mphase">
                  <h3 className="hta-mphase__title">Choose a Session Plan</h3>
                  <p className="hta-mphase__sub">
                    {showFree
                      ? "Your first HRTalk is free. Slot selection unlocks after confirmation."
                      : "Select a plan — slot selection opens after payment."}
                  </p>
                  {showFree && freePlan && (
                    <button onClick={() => setPlan(freePlan)} style={{
                      display: "flex", alignItems: "center", gap: 12, width: "100%",
                      padding: "14px 18px", borderRadius: 12, marginBottom: 10,
                      border: `2px solid ${plan?.id === freePlan.id ? T.brand : "#c6d48a"}`,
                      background: plan?.id === freePlan.id ? "#f2f5ea" : "#f8faf0",
                      textAlign: "left", cursor: "pointer", fontFamily: T.font, transition: "all .18s",
                    }}>
                      <span style={{ fontSize: "1.4rem" }}>{freePlan.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: ".86rem", color: T.ink1 }}>{freePlan.name}</div>
                        <div style={{ fontSize: ".74rem", color: T.ink4, marginTop: 2 }}>{freePlan.desc}</div>
                      </div>
                      <strong style={{ fontSize: ".88rem", color: T.green }}>Free</strong>
                    </button>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {paidPlans.map((item) => (
                      <button key={item.id} onClick={() => setPlan(item)} style={{
                        display: "flex", alignItems: "center", gap: 12, width: "100%",
                        padding: "14px 18px", borderRadius: 12,
                        border: `2px solid ${plan?.id === item.id ? T.brand : T.rule}`,
                        background: plan?.id === item.id ? "#f2f5ea" : T.surface,
                        textAlign: "left", cursor: "pointer", fontFamily: T.font,
                        transition: "all .18s", position: "relative",
                      }}>
                        {item.popular && (
                          <span style={{
                            position: "absolute", top: -8, right: 12,
                            background: T.brand, color: "#fff",
                            fontSize: ".6rem", fontWeight: 800, letterSpacing: ".08em",
                            padding: "2px 10px", borderRadius: 999,
                          }}>POPULAR</span>
                        )}
                        <span style={{ fontSize: "1.4rem" }}>{item.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: ".86rem", color: T.ink1 }}>{item.name}</div>
                          <div style={{ fontSize: ".74rem", color: T.ink5, marginTop: 2 }}>{item.desc}</div>
                        </div>
                        <strong style={{ fontSize: ".88rem", color: T.ink1 }}>{item.price}</strong>
                      </button>
                    ))}
                  </div>
                  {!plans.length && (
                    <div style={S.errorBox}>No plans available. Please contact admin.</div>
                  )}
                  <button className="hta-btn-primary" disabled={!plan} onClick={() => setPhase("confirm")}>
                    Continue →
                  </button>
                </div>
              )}

              {phase === "confirm" && (
                <div className="hta-mphase">
                  <button className="hta-back-btn" onClick={() => setPhase("planselect")}>← Back</button>
                  <h3 className="hta-mphase__title">Your Details</h3>
                  <p className="hta-mphase__sub">These appear in your booking confirmation email.</p>
                  <div className="hta-confirm-card" style={{ marginBottom: 14 }}>
                    <div className="hta-confirm-row"><span>Selected Plan</span><strong>{plan?.name}</strong></div>
                    <div className="hta-confirm-row"><span>Duration</span><strong>{plan?.durationMins} min</strong></div>
                    <div className="hta-confirm-row">
                      <span>Amount</span>
                      <strong style={{ color: plan?.isFree ? T.green : T.ink1 }}>{plan?.price}</strong>
                    </div>
                  </div>
                  <div className="hta-email-form" style={{ marginBottom: 14 }}>
                    <label htmlFor="hrt-name">Your full name</label>
                    <input id="hrt-name" className="sm-inp" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name" />
                    <label htmlFor="hrt-email" style={{ marginTop: 8 }}>Your email</label>
                    <input id="hrt-email" className="sm-inp" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com" />
                  </div>
                  {errMsg && <div style={S.errorBox}>{errMsg}</div>}
                  <button className="hta-btn-primary" disabled={!canSubmit}
                    onClick={() => setPhase("payment")}>
                    {plan?.isFree ? "Confirm & Continue →" : `Proceed to Pay ${plan?.price} →`}
                  </button>
                </div>
              )}

              {phase === "payment" && plan && (
                <PaymentStep hr={hr} plan={plan} name={name} email={email}
                  onBack={() => setPhase("confirm")} onSuccess={handlePaymentSuccess}
                  externalError={errMsg} />
              )}

              {phase === "timeslot" && (
                <>
                  <SlotStep slots={slots} plan={plan} onSelect={handleSlotConfirmed} sending={sending} />
                  {errMsg && <div style={S.errorBox}>{errMsg}</div>}
                </>
              )}

              {phase === "done" && bookingInfo && (
                <ConfirmedPhase hr={hr} slot={bookingInfo.slot} plan={plan}
                  name={name} email={email} paymentId={paymentInfo?.paymentId || null}
                  bookingId={bookingInfo.bookingId} onClose={onClose} />
              )}
            </>
          ) : (
            <div className="hta-mphase">
              <LoginGate action="schedule a session" onLoginClick={onTriggerLogin} />
              <p style={{ fontSize: ".74rem", color: T.ink4, marginTop: 8, textAlign: "center", fontFamily: T.font }}>
                Create an account or log in as a candidate to book your HRTalk session.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
ScheduleModal.propTypes = {
  hr:             PropTypes.object.isRequired,
  plans:          PropTypes.array.isRequired,
  slots:          PropTypes.array.isRequired,
  freeEligible:   PropTypes.bool.isRequired,
  onClose:        PropTypes.func.isRequired,
  onBooked:       PropTypes.func,
  currentUser:    PropTypes.object,
  onTriggerLogin: PropTypes.func.isRequired,
};
ScheduleModal.defaultProps = { onBooked: null, currentUser: null };

/* ─── HR CARD ─── */
function HRCard({ hr, onSchedule, onReadReview, delay, startingPriceLabel, userBookings }) {
  const statusColor = { online: T.green, away: "#f59e0b", busy: T.red }[hr.status] || T.ink5;
  const statusLabel = { online: "Available Now", away: "Away", busy: "Busy" }[hr.status] || "Offline";
  const hasPending  = userBookings?.some(b =>
    b.hr_id === hr.id && (b.status === "pending" || b.status === "confirmed")
  );

  return (
    <div className="hta-hr-card" data-aos="fade-up" data-aos-delay={delay} style={{ "--acc": hr.accent }}>
      <div className="hta-hr-card__bar" />
      {hr.badge && (
        <div className={`hta-hr-badge hta-hr-badge--${hr.badge === "Top Rated" ? "top" : "new"}`}>
          {hr.badge}
        </div>
      )}
      {hasPending && (
        <div className="hta-card-pending-notice"><span>🕐</span> Session pending</div>
      )}
      <div className="hta-hr-card__body">
        <div className="hta-hr-card__head">
          <div className="hta-hr-ava-container">
            <Avatar name={hr.name} avatarUrl={hr.avatarUrl} size={64} radius={16} className="hta-hr-ava-img" />
            <span className="hta-hr-ava__dot" style={{ background: statusColor }} />
          </div>
          <div className="hta-hr-card__info">
            <div className="hta-hr-card__name">{hr.name}</div>
            <div className="hta-hr-card__role">{hr.role}</div>
            <div className="hta-hr-avail-inline">
              <span className="hta-hr-avail__dot" style={{ background: statusColor }} />
              <span style={{ color: statusColor, fontSize: ".7rem", fontWeight: 600 }}>{statusLabel}</span>
            </div>
          </div>
        </div>
        <div className="hta-hr-card__tags" style={{ marginTop: 10 }}>
          {(hr.tags || []).map((tag) => <span key={tag} className="hta-hr-tag">{tag}</span>)}
        </div>
        <div className="hta-hr-stats">
          {[
            ["Sessions", Number(hr.sessions || 0).toLocaleString("en-IN")],
            ["Exp.",     hr.experience || "-"],
            ["Reviews",  Number(hr.reviews || 0)],
          ].map(([label, value]) => (
            <div key={label} className="hta-hr-stat">
              <div className="hta-hr-stat__val">{value}</div>
              <div className="hta-hr-stat__lbl">{label}</div>
            </div>
          ))}
        </div>
        <div className="hta-hr-card__rating">
          <Stars n={hr.rating || 0} />
          <span className="hta-hr-revs">({Number(hr.reviews || 0)} ratings)</span>
        </div>
        <p className="hta-hr-card__bio">{hr.bio}</p>
        <div className="hta-hr-card__footer">
          <span className="hta-hr-pricing-free">{startingPriceLabel}</span>
        </div>
        <div className="hta-hr-card__cta-row">
          <button
            className="hta-hr-card__cta hta-hr-card__cta--primary"
            onClick={() => onSchedule(hr)}
          >
            {hasPending ? "View Session" : "Schedule Meet"}
          </button>
          <button
            className="hta-hr-card__cta hta-hr-card__cta--outline"
            onClick={() => onReadReview(hr)}
          >
            Read Reviews
          </button>
        </div>
      </div>
    </div>
  );
}
HRCard.propTypes = {
  hr:                 PropTypes.object.isRequired,
  onSchedule:         PropTypes.func.isRequired,
  onReadReview:       PropTypes.func.isRequired,
  delay:              PropTypes.number,
  startingPriceLabel: PropTypes.string,
  userBookings:       PropTypes.array,
};
HRCard.defaultProps = { delay: 0, startingPriceLabel: "", userBookings: [] };

/* ─── HOW IT WORKS ─── */
function HowItWorks() {
  const [activeStep,   setActiveStep]   = useState(null);
  const [visibleSteps, setVisibleSteps] = useState([]);
  const [tick,         setTick]         = useState(0);
  const cardRefs  = useRef([]);
  const autoTimer = useRef(null);

  useEffect(() => {
    autoTimer.current = setInterval(() => setTick((t) => t + 1), 2200);
    return () => clearInterval(autoTimer.current);
  }, []);

  const autoActive = tick % HOW_IT_WORKS.length;

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idx = cardRefs.current.indexOf(entry.target);
          if (idx !== -1) {
            setTimeout(() => setVisibleSteps((prev) => prev.includes(idx) ? prev : [...prev, idx]), idx * 140);
          }
        }
      });
    }, { threshold: 0.12 });
    cardRefs.current.forEach((c) => c && io.observe(c));
    return () => io.disconnect();
  }, []);

  const active = activeStep !== null ? activeStep : autoActive;

  const handleStepClick = (i, isActive) => {
    setActiveStep(isActive ? null : i);
    clearInterval(autoTimer.current);
  };

  const handleStepKeyDown = (e, i, isActive) => {
    if (e.key === "Enter" || e.key === " ") {
      setActiveStep(isActive ? null : i);
      clearInterval(autoTimer.current);
    }
  };

  const handlePipClick = (i) => {
    setActiveStep(i);
    clearInterval(autoTimer.current);
  };

  const handlePipKeyDown = (e, i) => {
    if (e.key === "Enter" || e.key === " ") {
      setActiveStep(i);
      clearInterval(autoTimer.current);
    }
  };

  return (
    <section className="hiw2-section" id="how-it-works">
      <div className="hiw2-bg" aria-hidden="true">
        <div className="hiw2-bg__orb hiw2-bg__orb--1" />
        <div className="hiw2-bg__orb hiw2-bg__orb--2" />
        <div className="hiw2-bg__orb hiw2-bg__orb--3" />
        <div className="hiw2-bg__grid" />
        {Array.from({ length: 14 }, (_, i) => (
          <div key={`particle-${i}`} className="hiw2-particle" style={{ "--pi": i }} />
        ))}
      </div>
      <div className="hiw2-header">
        <h2 className="hiw2-title">Book your <span className="hiw2-title__em">HRTalk</span><br />in four steps</h2>
        <p className="hiw2-subtitle">From discovery to career growth — seamless, guided, and personal.</p>
      </div>
      <div className="hiw2-progress-bar">
        {HOW_IT_WORKS.map((step, i) => (
          <div key={step.num}
            className={`hiw2-progress-pip ${i === active ? "hiw2-progress-pip--on" : ""} ${i < active ? "hiw2-progress-pip--done" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Go to step ${i + 1}`}
            onClick={() => handlePipClick(i)}
            onKeyDown={(e) => handlePipKeyDown(e, i)}
          />
        ))}
        <div className="hiw2-progress-fill" style={{ width: `${((active + 1) / HOW_IT_WORKS.length) * 100}%` }} />
      </div>
      <div className="hiw2-grid">
        <div className="hiw2-connector">
          <div className="hiw2-connector__fill" style={{ width: `${(active / (HOW_IT_WORKS.length - 1)) * 100}%` }} />
        </div>
        {HOW_IT_WORKS.map((item, i) => {
          const isActive  = i === active;
          const isDone    = i < active;
          const isVisible = visibleSteps.includes(i);
          return (
            <div key={item.num}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={`hiw2-step ${isActive ? "hiw2-step--active" : ""} ${isDone ? "hiw2-step--done" : ""} ${isVisible ? "hiw2-step--visible" : ""}`}
              style={{ "--delay": `${i * 0.12}s`, "--accent": COLORS[i] }}
              role="button"
              tabIndex={0}
              aria-label={`Step ${item.num}: ${item.title}`}
              onClick={() => handleStepClick(i, isActive)}
              onKeyDown={(e) => handleStepKeyDown(e, i, isActive)}
            >
              <div className="hiw2-bubble">
                <div className="hiw2-bubble__ring" />
                <div className="hiw2-bubble__ring hiw2-bubble__ring--2" />
                <span className="hiw2-bubble__num">{isDone ? "✓" : item.num}</span>
              </div>
              <div className="hiw2-card">
                <div className="hiw2-card__glow" />
                <div className="hiw2-card__top">
                  <span className="hiw2-card__icon">{item.icon}</span>
                  <span className="hiw2-card__tag">{item.tag}</span>
                </div>
                <h3 className="hiw2-card__title">{item.title}</h3>
                <p className="hiw2-card__desc">{item.desc}</p>
                <div className={`hiw2-card__detail ${isActive ? "hiw2-card__detail--open" : ""}`}>
                  <p className="hiw2-card__detail-text">{item.detail}</p>
                </div>
                <div className="hiw2-card__bar" />
              </div>
            </div>
          );
        })}
      </div>
      <div className="hiw2-cta">
        <p className="hiw2-cta__sub">Ready to talk to a real HR expert?</p>
        <button className="hiw2-cta__btn">
          <span>Book Your Session</span>
          <span className="hiw2-cta__arrow">→</span>
        </button>
        <p className="hiw2-cta__note">First session free · No credit card needed</p>
      </div>
    </section>
  );
}

/* ─── ROOT COMPONENT ─── */
export default function HRTalk() {
  const [search,        setSearch]        = useState("");
  const [status,        setStatus]        = useState("all");
  const [activeHR,      setActiveHR]      = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [reviewHR,      setReviewHR]      = useState(null);
  const [showReviews,   setShowReviews]   = useState(false);
  const [openLogin,     setOpenLogin]     = useState(false);

  const [experts,       setExperts]       = useState([]);
  const [plansRaw,      setPlansRaw]      = useState([]);
  const [timings,       setTimings]       = useState([]);
  const [freeEligible,  setFreeEligible]  = useState(false);
  const [userBookings,  setUserBookings]  = useState([]);
  const [currentUser,   setCurrentUser]   = useState(null);
  const [reviews,       setReviews]       = useState([]);

  const [loadingConfig,  setLoadingConfig]  = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [configError,    setConfigError]    = useState("");

  useAOS([experts, plansRaw]);

  /* ── Auth ── */
  useEffect(() => {
    const syncAuth = () => {
      const token = getToken();
      const u = token ? getStoredUser() : null;
      setCurrentUser(u);
      if (u && token) {
        hrtalkFetch("/api/hrtalk/my-bookings", { auth: true })
          .then(data => setUserBookings(Array.isArray(data.bookings) ? data.bookings : []))
          .catch(() => setUserBookings([]));
      } else {
        setUserBookings([]);
      }
    };
    syncAuth();
    window.addEventListener("auth:update", syncAuth);
    window.addEventListener("storage", syncAuth);
    return () => {
      window.removeEventListener("auth:update", syncAuth);
      window.removeEventListener("storage", syncAuth);
    };
  }, []);

  const isLoggedIn = Boolean(currentUser && getToken());

  /* ── Config ── */
  const loadConfig = useCallback(async () => {
    setLoadingConfig(true);
    setConfigError("");
    try {
      const payload = await hrtalkFetch("/api/hrtalk/config");
      const apiExperts = (Array.isArray(payload.experts) ? payload.experts : []).map((e) => ({
        ...e,
        avatarUrl: e.avatar_url || e.avatar || e.image_url || null,
        rating: Number(e.avg_rating || e.rating || 0),
        reviews: Number(e.rating_count || e.reviews || 0),
        tags: Array.isArray(e.tags)
          ? e.tags
          : (() => { try { return JSON.parse(e.tags || "[]"); } catch { return []; } })(),
      }));
      setExperts(apiExperts);
      setPlansRaw(Array.isArray(payload.plans)   ? payload.plans   : []);
      setTimings( Array.isArray(payload.timings) ? payload.timings : []);
      setFreeEligible(Boolean(payload.freeEligible));
    } catch (err) {
      setConfigError(err.message || "Failed to load HRTalk data. Please refresh.");
      setExperts([]);
      setPlansRaw([]);
      setTimings([]);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  /* ── All public reviews ── */
  useEffect(() => {
    setLoadingReviews(true);
    hrtalkFetch("/api/hrtalk/reviews")
      .then(data => setReviews(Array.isArray(data.reviews) ? data.reviews : []))
      .catch(() => setReviews([]))
      .finally(() => setLoadingReviews(false));
  }, []);

  /* ── Derived: plans ── */
  const plans = useMemo(() => {
    return plansRaw.map((p) => ({
      dbId:        Number(p.id || 0),
      id:          String(p.code || p.plan_code || `plan-${p.id}`),
      icon:        p.icon || "⚡",
      name:        p.name || p.plan_name || "Plan",
      desc:        p.description || `${Number(p.durationMins || p.duration_mins || 30)} minute session`,
      price:       formatMoney(Number(p.priceInr || p.price_inr || 0)),
      priceInr:    Number(p.priceInr || p.price_inr || 0),
      amountPaise: Number(p.priceInr || p.price_inr || 0) * 100,
      durationMins: Number(p.durationMins || p.duration_mins || 30),
      isFree:      Boolean(p.isFree || p.is_free),
      popular:     Boolean(p.isPopular || p.is_popular),
    }));
  }, [plansRaw]);

  /* ── Derived: slots ── */
  const slots = useMemo(() => {
    const slotDuration = plans.find(p => !p.isFree)?.durationMins || 30;
    return buildSlotsFromTimings(timings, slotDuration);
  }, [timings, plans]);

  /* ── Derived: pricing label ── */
  const paidPlans  = plans.filter((p) => !p.isFree);
  const lowestPaid = paidPlans.length
    ? Math.min(...paidPlans.map((p) => Number(p.priceInr || 0)).filter(v => v >= 0))
    : 0;
  const startLabel = paidPlans.length ? `From ${formatMoney(lowestPaid)}` : "Plans by admin";

  /* ── Filtered experts ── */
  const filtered = experts.filter((expert) => {
    const q = search.toLowerCase();
    const matchesSearch = !q
      || String(expert.name  || "").toLowerCase().includes(q)
      || String(expert.role  || "").toLowerCase().includes(q)
      || (expert.tags || []).some((t) => String(t).toLowerCase().includes(q));
    const matchesStatus = status === "all" || expert.status === status;
    return matchesSearch && matchesStatus;
  });

  /* ── Reviews grouped by expert ── */
  const reviewsByExpert = useMemo(() => {
    const map = {};
    reviews.forEach(r => {
      const key = r.hr_id;
      if (key == null) return;
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }, [reviews]);

  /* ── Handlers ── */
  // Re-check token at click time to avoid stale isLoggedIn
  const handleOpenSchedule = (hr) => {
    const token = getToken();
    const user  = getStoredUser();
    if (!token || !user) {
      setOpenLogin(true);
      return;
    }
    setActiveHR(hr);
    setShowModal(true);
  };

  const handleOpenReviews   = (hr) => { setReviewHR(hr); setShowReviews(true); };
  const handleCloseSchedule = () => { setShowModal(false); setActiveHR(null); document.body.style.overflow = ""; };
  const handleCloseReviews  = () => { setShowReviews(false); setReviewHR(null); document.body.style.overflow = ""; };

  const pendingSessions = useMemo(
    () => userBookings.filter(b => b.status === "pending" || b.status === "confirmed"),
    [userBookings]
  );

  /* ── Login success handler — re-sync auth immediately ── */
  const handleLoginSuccess = () => {
    setOpenLogin(false);
    const token = getToken();
    const u = token ? getStoredUser() : null;
    if (u && token) {
      setCurrentUser(u);
      hrtalkFetch("/api/hrtalk/my-bookings", { auth: true })
        .then(data => setUserBookings(Array.isArray(data.bookings) ? data.bookings : []))
        .catch(() => setUserBookings([]));
    }
    window.dispatchEvent(new Event("auth:update"));
  };

  return (
    <div className="hta-root">

      {/* ── HERO ── */}
      <section className="hta-hero" data-aos="fade-up">
        <div className="hta-hero__bg" aria-hidden="true">
          <div className="hta-hero__grid" />
        </div>
        <div className="hta-hero__container">
          <div className="hta-hero__left" data-aos="fade-right">
            <h1 className="hta-hero__title" data-aos="fade-up" data-aos-delay="60">
              Schedule a Meet with<br /><em>HR Experts</em>
            </h1>
            <p className="hta-hero__sub" data-aos="fade-up" data-aos-delay="90">
              Personalized guidance from verified HR professionals.
              <strong className="nowrap"> First session free. Slot booking unlocks after payment.</strong>
            </p>
            {configError && (
              <div style={{ ...S.errorBox, marginBottom: 12 }}>{configError}</div>
            )}
            <div className="hta-hero__ctas" data-aos="fade-up" data-aos-delay="120">
              <button className="hta-hero__cta-primary"
                onClick={() => document.getElementById("ht-hr-section")?.scrollIntoView({ behavior: "smooth" })}>
                Browse Experts
              </button>
              <button className="hta-hero__cta-ghost"
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>
                How It Works
              </button>
            </div>
          </div>

          <div className="hta-hero__right" data-aos="fade-left" data-aos-delay="100">
            <div className="hta-hero__preview">
              {loadingConfig
                ? [0,1,2].map(i => (
                    <div key={`hero-skel-${i}`} className="hta-hero__preview-card" style={{ animationDelay: `${i * 0.14}s` }}>
                      <div className="sk-circle" style={{ width: 48, height: 48, borderRadius: 13 }} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                        <div className="sk-line" style={{ width: "55%", height: 12 }} />
                        <div className="sk-line" style={{ width: "75%", height: 10 }} />
                      </div>
                    </div>
                  ))
                : experts.slice(0, 3).map((hr, i) => (
                    <div key={hr.id} className="hta-hero__preview-card"
                      data-aos={i % 2 === 0 ? "fade-left" : "fade-right"}
                      data-aos-delay={140 + i * 60}
                      style={{ "--acc": hr.accent, animationDelay: `${i * 0.14}s` }}>
                      <div className="hta-hero__preview-ava-wrap">
                        <Avatar name={hr.name} avatarUrl={hr.avatarUrl} size={48} radius={13}
                          style={{ boxShadow: "0 3px 12px rgba(0,0,0,.15)" }} />
                        <span className="hta-hero__preview-dot"
                          style={{ background: hr.status === "online" ? T.green : T.ink5 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="hta-hero__preview-name">{hr.name}</div>
                        <div className="hta-hero__preview-role">{hr.role} · {hr.experience}</div>
                      </div>
                      <div className="hta-hero__preview-rating">{Number(hr.rating || 0).toFixed(1)}</div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      </section>

      {/* ── PENDING SESSIONS BANNER ── */}
      {currentUser && <PendingSessionBanner bookings={userBookings} experts={experts} />}

      {/* ── SEARCH + FILTER ── */}
      <div className="hta-filter-bar" id="ht-hr-section" data-aos="fade-up">
        <div className="hta-filter-bar__inner">
          <div className="hta-search-box">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="5.5" stroke="#263012" strokeWidth="1.5" />
              <path d="M11 11L14 14" stroke="#263012" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input type="text" placeholder="Search by name, role or skill…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && (
              <button className="hta-search-clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>
          <div className="hta-filter-pills-wrap">
            <div className="hta-filter-pills">
              {["all", "online", "away", "busy"].map(s => (
                <button key={s}
                  className={`hta-filter-pill ${status === s ? "hta-filter-pill--active" : ""}`}
                  onClick={() => setStatus(s)}>
                  {s === "all" ? "All" : s === "online" ? "✅ Available" : s === "away" ? "🌙 Away" : "🔴 Busy"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN GRID ── */}
      <div className="hta-main" data-aos="fade-up">
        <div className="hta-main__primary">
          <div className="hta-list-head" data-aos="fade-up" data-aos-delay="20">
            <h2 className="hta-list-head__title">Choose Your HR Expert</h2>
            <p className="hta-list-head__sub">Compare expertise, availability and session focus before booking.</p>
          </div>
          {loadingConfig ? (
            <div className="hta-hr-grid">
              {[0,1,2,3].map(i => <CardSkeleton key={`card-skel-${i}`} />)}
            </div>
          ) : configError ? (
            <ErrorState message={configError} onRetry={loadConfig} />
          ) : filtered.length === 0 ? (
            <EmptyState
              title={search || status !== "all" ? "No experts match your filter" : "No experts available"}
              sub={search || status !== "all"
                ? "Try changing search or availability filters."
                : "Please check back soon."}
            />
          ) : (
            <div className="hta-hr-grid">
              {filtered.map((hr, i) => (
                <HRCard key={hr.id} hr={hr}
                  onSchedule={handleOpenSchedule}
                  onReadReview={handleOpenReviews}
                  delay={i * 60} startingPriceLabel={startLabel}
                  userBookings={userBookings} />
              ))}
            </div>
          )}
        </div>

        {/* ── SIDEBAR ── */}
        <aside className="hta-sidebar" data-aos="fade-left" data-aos-delay="40">
          <div className="hta-sidebar-card hta-sidebar-plans">
            <div className="hta-sidebar-card__title"><span>💼</span> Session Plans</div>
            {freeEligible && (
              <div className="hta-sidebar-free-badge">🎁 Your first session is free!</div>
            )}
            {loadingConfig ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[0,1,2,3].map(i => (
                  <div key={`plan-skel-${i}`} className="sk-line" style={{ height: 52, borderRadius: 10 }} />
                ))}
              </div>
            ) : plans.length === 0 ? (
              <div style={{ fontSize: ".75rem", color: T.ink5, padding: "8px 0" }}>
                No plans configured yet.
              </div>
            ) : (
              <div className="hta-sidebar-plans-list">
                {plans.map((p) => (
                  <div key={p.id}
                    className={`hta-plan-item ${p.popular ? "hta-plan-item--pop" : ""} ${p.isFree ? "hta-plan-item--free" : ""}`}>
                    <div className="hta-plan-item__left">
                      <span className="hta-plan-item__icon">{p.icon}</span>
                      <div>
                        <div className="hta-plan-name">
                          {p.name}
                          {p.popular && <span className="hta-pop-tag">Popular</span>}
                          {p.isFree  && <span className="hta-pop-tag hta-pop-tag--free">Free</span>}
                        </div>
                        <div className="hta-plan-desc">{p.desc}</div>
                      </div>
                    </div>
                    <div className={`hta-plan-price ${p.isFree ? "hta-plan-price--green" : ""}`}>
                      {p.price}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hta-sidebar-card">
            <div className="hta-sidebar-card__title"><span>⭐</span> Expert Spotlight</div>
            {loadingConfig
              ? [0,1,2].map(i => (
                  <div key={`spot-skel-${i}`} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
                    <div className="sk-circle" style={{ width: 40, height: 40, borderRadius: 10 }} />
                    <div style={{ flex: 1 }}>
                      <div className="sk-line" style={{ width: "60%", height: 12, marginBottom: 5 }} />
                      <div className="sk-line" style={{ width: "80%", height: 10 }} />
                    </div>
                  </div>
                ))
              : experts.slice(0, 3).map((hr) => (
                  <button key={hr.id} className="hta-sidebar-expert" type="button"
                    aria-label={`Schedule with ${hr.name}`}
                    onClick={() => handleOpenSchedule(hr)}>
                    <Avatar name={hr.name} avatarUrl={hr.avatarUrl} size={40} radius={10} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="hta-sidebar-expert__name">{hr.name}</div>
                      <div className="hta-sidebar-expert__role">{hr.role}</div>
                    </div>
                    <div className="hta-sidebar-expert__rating">
                      <span style={{ color: "#f59e0b" }}>★</span>
                      <span style={{ color: T.brand, fontWeight: 700, fontSize: ".78rem" }}>
                        {Number(hr.rating || 0).toFixed(1)}
                      </span>
                    </div>
                  </button>
                ))
            }
          </div>

          {currentUser && pendingSessions.length > 0 && (
            <div className="hta-sidebar-card hta-sidebar-pending">
              <div className="hta-sidebar-card__title"><span>🕐</span> Your Sessions</div>
              {pendingSessions.map((b) => {
                const expert = experts.find(e => e.id === b.hr_id);
                return (
                  <div key={b.id ?? b.slot?.slot_key ?? b.hr_id} className="hta-sidebar-session-item">
                    <div className="hta-sidebar-session-item__name">{expert?.name || "HR Expert"}</div>
                    <div className="hta-sidebar-session-item__time">
                      {b.day_label || b.slot?.day_label} · {b.time_label || b.slot?.time_label}
                    </div>
                    <span className="hta-sidebar-session-badge">Pending</span>
                  </div>
                );
              })}
            </div>
          )}
        </aside>
      </div>

      {/* ── HOW IT WORKS ── */}
      <HowItWorks />

      {/* ── PRICING ── */}
      <section className="hta-pricing" data-aos="fade-up">
        <div className="hta-pricing__inner">
          <div className="hta-section-head ht-section-head--center" data-aos="fade-up">
            <h2 className="hta-section-title">Admin-Controlled Session Plans</h2>
            <p className="hta-section-sub" style={{ textAlign: "center", margin: "0 auto" }}>
              Slot selection opens only after payment is confirmed.
            </p>
          </div>
          {loadingConfig ? (
            <div className="hta-pricing-grid">
              {[0,1,2].map(i => (
                <div key={`price-skel-${i}`} className="hta-price-card">
                  <div className="sk-line" style={{ width: 48, height: 48, borderRadius: 12, marginBottom: 12 }} />
                  <div className="sk-line" style={{ width: "60%", height: 16, marginBottom: 8 }} />
                  <div className="sk-line" style={{ width: "40%", height: 28, marginBottom: 10 }} />
                  <div className="sk-line" style={{ width: "80%", height: 11, marginBottom: 6 }} />
                  <div className="sk-line" style={{ width: "70%", height: 11, marginBottom: 6 }} />
                  <div className="sk-line" style={{ width: "75%", height: 11 }} />
                </div>
              ))}
            </div>
          ) : paidPlans.length === 0 ? (
            <EmptyState icon="💼" title="No plans configured" sub="The admin hasn't added plans yet." />
          ) : (
            <div className="hta-pricing-grid">
              {paidPlans.map((p, i) => (
                <div key={p.id}
                  className={`hta-price-card ${p.popular ? "hta-price-card--pop" : ""}`}
                  data-aos="fade-up" data-aos-delay={i * 80}>
                  {p.popular && <div className="hta-price-card__ribbon">Popular</div>}
                  <div className="hta-price-card__icon" style={{ fontSize: "1.4rem" }}>{p.icon}</div>
                  <div className="hta-price-card__name">{p.name}</div>
                  <div className="hta-price-card__amount">{p.price}</div>
                  <div className="hta-price-card__note">{p.desc}</div>
                  <div className="hta-price-feat">Duration: {p.durationMins} minutes</div>
                  <div className="hta-price-feat">Slot selection unlocked after payment</div>
                  <div className="hta-price-feat">Email confirmation to candidate &amp; HR</div>
                  <div className="hta-price-feat">Payment recorded in admin panel</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section className="hta-reviews" data-aos="fade-up">
        <div className="hta-section-head ht-section-head--center" data-aos="fade-up">
          <h2 className="hta-section-title">Real Ratings After Sessions</h2>
          <p className="hta-section-sub" style={{ textAlign: "center", maxWidth: 480, margin: "0 auto" }}>
            Verified reviews from candidates who have completed their HRTalk sessions.
          </p>
        </div>
        {loadingReviews ? (
          <div className="hta-reviews__mask" style={{ marginTop: 32 }}>
            <div className="hta-reviews__track">
              {[0,1,2,3,4,5].map(i => (
                <div key={`rev-skel-${i}`} className="hta-review-card">
                  <div className="sk-line" style={{ width: "40%", height: 14, marginBottom: 10 }} />
                  <div className="sk-line" style={{ width: "100%", height: 11, marginBottom: 5 }} />
                  <div className="sk-line" style={{ width: "80%", height: 11, marginBottom: 14 }} />
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div className="sk-circle" style={{ width: 32, height: 32, borderRadius: "50%" }} />
                    <div>
                      <div className="sk-line" style={{ width: 80, height: 11, marginBottom: 4 }} />
                      <div className="sk-line" style={{ width: 60, height: 10 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : reviews.length === 0 ? (
          <EmptyState icon="💬" title="No reviews yet" sub="Be the first to share your HRTalk experience!" />
        ) : (
          <>
            <div className="hta-reviews-by-expert" data-aos="fade-up" data-aos-delay="30">
              {experts.map((expert, ei) => {
                const expertReviews = reviewsByExpert[expert.id] || [];
                if (!expertReviews.length) return null;
                return (
                  <div key={expert.id} className="hta-expert-review-group"
                    data-aos="fade-up" data-aos-delay={ei * 60}>
                    <div className="hta-expert-review-group__head">
                      <Avatar name={expert.name} avatarUrl={expert.avatarUrl} size={36} radius={10} />
                      <div>
                        <div className="hta-expert-review-group__name">{expert.name}</div>
                        <div className="hta-expert-review-group__role">{expert.role}</div>
                      </div>
                      <div className="hta-expert-review-group__rating">
                        <span style={{ color: "#f59e0b" }}>★</span> {Number(expert.rating || 0).toFixed(1)}
                      </div>
                    </div>
                    <div className="hta-expert-review-group__cards">
                      {expertReviews.slice(0, 3).map((review) => (
                        <div key={review.id} className="hta-review-card">
                          <div className="hta-review-card__stars">{"★".repeat(review.rating || 0)}</div>
                          <p className="hta-review-card__text">&ldquo;{review.review_text || review.text}&rdquo;</p>
                          <div className="hta-review-card__author">
                            <div className="hta-review-card__ava"
                              style={{ background: COLORS[review.id % COLORS.length] }}>
                              {(review.candidate_name || review.name || "C")[0]}
                            </div>
                            <div>
                              <div className="hta-review-card__name">
                                {review.candidate_name || review.name || "Candidate"}
                              </div>
                              <div className="hta-review-card__role">
                                {review.candidate_role || review.role || ""}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hta-reviews__mask" data-aos="fade-up" data-aos-delay="60" style={{ marginTop: 32 }}>
              <div className="hta-reviews__track">
                {[...reviews, ...reviews].map((review, idx) => (
                  <div key={`rev-scroll-${review.id}-${idx}`} className="hta-review-card">
                    <div className="hta-review-card__stars">{"★".repeat(review.rating || 0)}</div>
                    <p className="hta-review-card__text">&ldquo;{review.review_text || review.text}&rdquo;</p>
                    <div className="hta-review-card__author">
                      <div className="hta-review-card__ava" style={{ background: COLORS[idx % COLORS.length] }}>
                        {(review.candidate_name || review.name || "C")[0]}
                      </div>
                      <div>
                        <div className="hta-review-card__name">
                          {review.candidate_name || review.name || "Candidate"}
                        </div>
                        <div className="hta-review-card__role">
                          {review.candidate_role || review.role || ""}
                        </div>
                      </div>
                    </div>
                    {review.hr_name && (
                      <div className="hta-review-card__expert-tag">via {review.hr_name}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      {/* ── CTA ── */}
      <section className="hta-cta" data-aos="fade-up">
        <div className="hta-cta__inner" data-aos="zoom-in" data-aos-delay="40">
          <h2 className="hta-cta__title">One HRTalk Can Change Your Next Move</h2>
          <p className="hta-cta__sub" data-aos="fade-up" data-aos-delay="60">
            Pick a plan → pay → choose your slot → get confirmation by email. Simple.
          </p>
          <div className="hta-cta__btns" data-aos="fade-up" data-aos-delay="120">
            <button className="hta-cta__btn-main"
              onClick={() => document.getElementById("ht-hr-section")?.scrollIntoView({ behavior: "smooth" })}>
              Schedule HRTalk
            </button>
          </div>
        </div>
      </section>

      {/* ── SCHEDULE MODAL ── */}
      {showModal && activeHR && (
        <ScheduleModal
          hr={activeHR} plans={plans} slots={slots} freeEligible={freeEligible}
          currentUser={currentUser} userBookings={userBookings}
          onClose={handleCloseSchedule}
          onBooked={loadConfig}
          onTriggerLogin={() => setOpenLogin(true)}
        />
      )}

      {/* ── REVIEWS MODAL ── */}
      {showReviews && reviewHR && (
        <ReviewsModal
          hr={reviewHR}
          currentUser={currentUser}
          onClose={handleCloseReviews}
          onTriggerLogin={() => setOpenLogin(true)}
        />
      )}

      <Login
        isOpen={openLogin}
        onClose={() => setOpenLogin(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
}